import { FormEvent, useRef } from 'react';
import { EditBloomerSocietyRef } from '../organisms/EditBloomerSociety';
import { useMutation } from '@apollo/client';
import { notify, cancel } from '../../helpers/toastify';
import { FallbackError, WithErrorBoundary } from '@bloomays-lib/ui.shared';
import EditBloomerSociety from '../organisms/EditBloomerSociety';
import { UPSERT_BLOOMER_SOCIETY, ME } from '@bloomays-lib/adapter.api-bloomer';
import { useNavigate, useParams } from 'react-router-dom';
import { IMission, ISociety } from '@bloomays-lib/types.shared';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { useUserStore } from '../../stores';
import { styled } from '@mui/material/styles';

const BloomerSociety = () => {
  const { t } = useTranslation(['notify', 'common']);
  const formSociety = useRef<EditBloomerSocietyRef>(null);
  const dataMe = useUserStore((state) => state.user);
  const navigate = useNavigate();
  const [upsertBloomerSociety] = useMutation<{ upsertBloomerSociety: ISociety }>(UPSERT_BLOOMER_SOCIETY, {
    refetchQueries: [
      {
        query: ME,
      },
    ],
  });

  const { societyRecordId, missionRecordId } = useParams<{ societyRecordId: string; missionRecordId: string }>();
  const mission = dataMe?.bloomer?.mission?.find((mission: IMission) => {
    return mission.recordId === missionRecordId;
  });

  if (!mission) {
    return <FallbackError errorText={t('missionNotFound', { ns: 'common' })} />;
  }

  const society = mission.bloomerBillingSociety?.find((bloomerBillingSociety) => {
    return bloomerBillingSociety.recordId === societyRecordId;
  });
  // recordId is not null, so society should be found
  // recorId null when setting a new society on mission
  if (societyRecordId && !society) {
    return <FallbackError errorText={t('societyNotFound', { ns: 'common' })} />;
  }

  const submitForm = (e: FormEvent<HTMLFormElement>): void => {
    formSociety?.current?.validate(e);
  };

  const upsertData = async (society: ISociety) => {
    try {
      notify('info', `${t('updateInProgress')}`, null, {
        hideProgressBar: false,
        autoClose: 10000,
      });
      const upload = await upsertBloomerSociety({
        variables: {
          input: {
            name: society.name,
            recordId: society.recordId,
            status: society.status,
            siret: society.siret,
            KBIS: society.KBIS,
            KbisExpirationDate: society.KbisExpirationDate,
            URSSAFVigilanceCertificate: society.URSSAFVigilanceCertificate,
            URSSAFVCExpirationDate: society.URSSAFVCExpirationDate,
            RCPVigilanceCertificate: society.RCPVigilanceCertificate,
            RCPExpirationDate: society.RCPExpirationDate,
            IBAN: society.IBAN,
            BIC: society.BIC,
            portage: society.portage,
            bloomerBillingMission: missionRecordId ? [missionRecordId] : undefined,
            tva: society.tva,
            addressCountryCode: society.addressCountryCode,
          },
        },
      });
      cancel();
      notify('success', `${t('updateSuccess')}`, null, {
        hideProgressBar: true,
        autoClose: 2000,
      });
      navigate('/');
      return upload?.data?.upsertBloomerSociety;
    } catch (error: any) {
      cancel();
      notify('error', `${t('randomUpdateError')}`, error);
      errorLogger(error, {
        extraInfos: 'extraErrorInfos',
      });
    }
  };

  return (
    <Container>
      <FormContainer onSubmit={submitForm}>
        <EditBloomerSociety ref={formSociety} onValidated={upsertData} society={society} />
      </FormContainer>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '55%',
  [theme.breakpoints.down('sm')]: {
    width: '95%',
  },
  justifyContent: 'center',
  margin: '0 auto', // Pour centrer horizontalement
}));

const FormContainer = styled('form')(({ theme }) => ({
  width: '100%',
}));

export default WithErrorBoundary(BloomerSociety);
